import Decimal from 'decimal.js-light';
import { useCallback, useMemo } from 'react';

import { getFiatFormatter } from '@/lib/formatting';
import type { Amount } from '@/lib/formatting/types';

import { useCurrencyStore, selectedCurrencySelector, fxRatesSelector } from '@/store/currency';

/**
 * Central hook for multi-currency fiat display.
 *
 * All internal prices remain in USD. This hook applies the FX rate
 * at the display boundary to convert and format into the user's selected currency.
 *
 * Falls back to USD display if the FX rate for the selected currency hasn't loaded yet.
 */
export function useFiatDisplay() {
  const selectedCurrency = useCurrencyStore(selectedCurrencySelector);
  const fxRates = useCurrencyStore(fxRatesSelector);

  // Fall back to USD if the rate for the selected currency hasn't loaded yet,
  // to avoid showing USD amounts with a non-USD symbol.
  const hasRate = selectedCurrency === 'USD' || fxRates[selectedCurrency] != null;
  const effectiveCurrency = hasRate ? selectedCurrency : 'USD';
  const fxRate = effectiveCurrency === 'USD' ? 1 : (fxRates[effectiveCurrency] ?? 1);
  const formatter = useMemo(() => getFiatFormatter(effectiveCurrency), [effectiveCurrency]);

  const formatFiat = useCallback(
    (usdAmount: Amount, showExtraSmallAmounts?: boolean, showDecimals?: boolean) => {
      if (fxRate === 1) return formatter.format(usdAmount, showExtraSmallAmounts, showDecimals);
      const converted = new Decimal(usdAmount ?? 0).mul(fxRate);
      return formatter.format(converted, showExtraSmallAmounts, showDecimals);
    },
    [formatter, fxRate],
  );

  const convertFromUsd = useCallback((usdAmount: number) => usdAmount * fxRate, [fxRate]);

  const currencySymbol = useMemo(() => formatter.getCurrencySymbol(), [formatter]);

  return {
    formatFiat,
    convertFromUsd,
    currencyCode: effectiveCurrency,
    currencySymbol,
    fxRate,
    formatter,
  };
}
